import React, { useState, useEffect } from 'react';
import {
    Card,
    CardBody,
    ButtonDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Form,
    FormGroup,
} from 'reactstrap';
import { ProfessionStandardsDesignationDto } from '../../../common/Types';
import styled from 'styled-components';
import { Input, Button } from '../../../components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { professionService } from '../../../services/ProfessionAdministration';
import SubStandardsCard from './SubStandardsCard';

const FlexRow = styled.div`
    display: flex;
    padding-top: 8px;
    padding-bottom: 8px;
    border-top: 1px solid #dee2e6;

    > div {
        line-height: 40px;
        height: 40px;
    }
`;

const EditRow = styled.div`
    border-top: 1px solid #dee2e6;
    padding-top: 16px;
    padding-bottom: 8px;
`;

type Props = {
    designations: ProfessionStandardsDesignationDto[] | undefined;
    professionId: number;
};

const DesignationCard = (props: Props) => {
    const [designations, setDesignations] = useState<ProfessionStandardsDesignationDto[]>([]);
    // track which button dropdown should be open by standardsDesignationId
    const [dropdownOpenId, setDropdownOpenId] = useState<number>();
    // track which edit form should be open by standardsDesignationId
    const [editId, setEditId] = useState<number>();
    const [deletedId, setDeleteId] = useState<number>();
    // track which designation is showing substandards
    const [designationWSubStandards, setDesignationWSubStandards] = useState<ProfessionStandardsDesignationDto>();

    const versionSchema = yup
        .object({
            standardsDesignationId: yup.number().required().default(0),
            professionId: yup.number().required().default(props.professionId),
            designationNumber: yup.string().required().default(''),
            designationTitle: yup.string().required().default(''),
            designationDescription: yup.string().required().default(''),
            createdBy: yup.string().optional().default('').transform((value) => value ?? ''),
            dateCreated: yup.string().optional().default('').transform((value) => value ?? ''),
            lastEdited: yup.string().optional().default('').transform((value) => value ?? ''),
            editedBy: yup.string().optional().default('').transform((value) => value ?? ''),
            isDeleted: yup.boolean().optional().default(false).transform((value) => value ?? false),
            createdByString: yup.string().optional().default('').transform((value) => value ?? ''),
            editedByString: yup.string().optional().default('').transform((value) => value ?? ''),
        })
        .defined();

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isSubmitting, errors },
    } = useForm({
        resolver: yupResolver(versionSchema),
        defaultValues: {
            standardsDesignationId: 0,
            professionId: props.professionId,
            designationNumber: '',
            designationTitle: '',
            designationDescription: '',
            createdBy: '',
            dateCreated: '',
            lastEdited: '',
            editedBy: '',
            isDeleted: false,
            createdByString: '',
            editedByString: '',
        },
    });

    const toggleButtonDropdown = (id: number) => {
        if (dropdownOpenId) setDropdownOpenId(undefined);
        else setDropdownOpenId(id);
    };

    const onSubmit = (formData: ProfessionStandardsDesignationDto): Promise<void> => {
        return new Promise((res) => {
            const toastId = toast.info('Saving Standard Designations...');

            if (formData.standardsDesignationId === deletedId) {
                formData.isDeleted = true;
                setDeleteId(undefined);
            }

            professionService
                .saveStandardDesignations([formData])
                .then((response) => {
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Standard Designations saved successfuly',
                    });
                    reset();
                    setEditId(undefined);
                    const replace = designations
                        .map((x) => {
                            if (x.standardsDesignationId === formData.standardsDesignationId) {
                                return formData.isDeleted ? ({} as ProfessionStandardsDesignationDto) : response[0];
                            } else {
                                return x;
                            }
                        })
                        .filter((y) => y.standardsDesignationId !== undefined);
                    setDesignations(replace);
                    res();
                })
                .catch(() => {
                    toast.update(toastId, {
                        type: 'error',
                        render: 'Error saving Standard Designations',
                    });
                    res();
                });
        });
    };

    const addDesignation = () => {
        const newDesignation = {
            standardsDesignationId: 0,
            professionId: props.professionId,
            designationNumber: '',
            designationTitle: '',
            designationDescription: '',
            createdBy: '',
            dateCreated: '',
            lastEdited: '',
            editedBy: '',
            isDeleted: false,
            createdByString: '',
            editedByString: '',
        } as ProfessionStandardsDesignationDto;
        
        setDesignations([newDesignation, ...designations]);
        setEditId(0);
        reset(newDesignation);
        // Explicitly set boolean value
        setValue('isDeleted', false);
    };

    const deleteDesignation = (des, index) => {
        // If it is a new designation just remove it from the list
        if (des.standardsDesignationId) setDeleteId(des.standardsDesignationId);
        else {
            // Delete by index in case use has added multiple
            setDesignations(designations.filter((x, i) => i !== index));
        }
    };

    useEffect(() => {
        if (props.designations?.length) {
            setDesignations(props.designations);
        }
    }, [props.designations, setDesignations]);

    const romanTable = {
        I: 1,
        II: 2,
        III: 3,
        IV: 4,
        V: 5,
        VI: 6,
        VII: 7,
        VIII: 8,
        IX: 9,
        X: 10,
        XI: 11,
        XII: 12,
    };

    return (
        <>
            {designationWSubStandards !== undefined ? (
                <SubStandardsCard
                    designation={designationWSubStandards}
                    close={() => setDesignationWSubStandards(undefined)}
                />
            ) : (
                <Card>
                    <CardBody>
                        <div style={{ display: 'flex' }} className="mb-3">
                            <div style={{ flex: 1 }}>
                                <h5>Designations</h5>
                            </div>
                            <div>
                                <Button onClick={() => addDesignation()}>Add Designation</Button>
                            </div>
                        </div>
                        <div>
                            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                                {designations &&
                                    designations
                                        .sort((a, b) => {
                                            const [mainA, secondaryA, tertiaryA, quaternaryA] =
                                                a.designationNumber?.split('.') || [];
                                            const [mainB, secondaryB, tertiaryB, quaternaryB] =
                                                b.designationNumber?.split('.') || [];

                                            if (mainA !== mainB) return romanTable[mainA] - romanTable[mainB];
                                            if (secondaryA !== secondaryB) return secondaryA?.localeCompare(secondaryB);
                                            if (tertiaryA !== tertiaryB) return tertiaryA?.localeCompare(tertiaryB);

                                            return quaternaryA?.localeCompare(quaternaryB);
                                        })
                                        .map((designation, i) => {
                                            return (
                                                <div key={i}>
                                                    {designation.standardsDesignationId === editId ? (
                                                        <EditRow>
                                                            <div style={{ display: 'flex' }}>
                                                                <FormGroup>
                                                                    <Controller
                                                                        name="designationNumber"
                                                                        control={control}
                                                                        render={({ field }) => (
                                                                            <Input
                                                                                placeholder="Number"
                                                                                {...field}
                                                                                value={field.value || ''}
                                                                                onChange={(e) => field.onChange(e.target.value || '')}
                                                                            />
                                                                        )}
                                                                    />
                                                                    {errors.designationNumber && (
                                                                        <div className="text-danger small">
                                                                            {errors.designationNumber.message}
                                                                        </div>
                                                                    )}
                                                                </FormGroup>
                                                                &nbsp;
                                                                <FormGroup style={{ flex: 1 }}>
                                                                    <Controller
                                                                        name="designationTitle"
                                                                        control={control}
                                                                        render={({ field }) => (
                                                                            <Input
                                                                                placeholder="Title"
                                                                                {...field}
                                                                                value={field.value || ''}
                                                                                onChange={(e) => field.onChange(e.target.value || '')}
                                                                            />
                                                                        )}
                                                                    />
                                                                    {errors.designationTitle && (
                                                                        <div className="text-danger small">
                                                                            {errors.designationTitle.message}
                                                                        </div>
                                                                    )}
                                                                </FormGroup>
                                                            </div>
                                                            <div>
                                                                <FormGroup>
                                                                    <Controller
                                                                        name="designationDescription"
                                                                        control={control}
                                                                        render={({ field }) => (
                                                                            <Input
                                                                                placeholder="Description"
                                                                                type="textarea"
                                                                                {...field}
                                                                                value={field.value || ''}
                                                                                onChange={(e) => field.onChange(e.target.value || '')}
                                                                            />
                                                                        )}
                                                                    />
                                                                    {errors.designationDescription && (
                                                                        <div className="text-danger small">
                                                                            {errors.designationDescription.message}
                                                                        </div>
                                                                    )}
                                                                </FormGroup>
                                                            </div>
                                                            <Controller
                                                                name="standardsDesignationId"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="hidden"
                                                                        {...field}
                                                                        value={field.value || 0}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                name="professionId"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="hidden"
                                                                        {...field}
                                                                        value={field.value || props.professionId}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                name="createdBy"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="hidden"
                                                                        {...field}
                                                                        value={field.value || ''}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                name="dateCreated"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="hidden"
                                                                        {...field}
                                                                        value={field.value || ''}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                name="lastEdited"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="hidden"
                                                                        {...field}
                                                                        value={field.value || ''}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                name="editedBy"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="hidden"
                                                                        {...field}
                                                                        value={field.value || ''}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                name="isDeleted"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="hidden"
                                                                        {...field}
                                                                        value={field.value ? 'true' : 'false'}
                                                                        onChange={(e) => field.onChange(e.target.value === 'true')}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                name="createdByString"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="hidden"
                                                                        {...field}
                                                                        value={field.value || ''}
                                                                    />
                                                                )}
                                                            />
                                                            <Controller
                                                                name="editedByString"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="hidden"
                                                                        {...field}
                                                                        value={field.value || ''}
                                                                    />
                                                                )}
                                                            />
                                                            <div style={{ display: 'flex' }}>
                                                                <div style={{ flex: 1 }}>
                                                                    <Button
                                                                        color="primary"
                                                                        type="submit"
                                                                        className={'mr-1'}
                                                                        disabled={isSubmitting}
                                                                    >
                                                                        Save
                                                                    </Button>
                                                                    {designation.standardsDesignationId !== 0 && (
                                                                        <Button
                                                                            outline
                                                                            onClick={() => setEditId(undefined)}
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    {deletedId ===
                                                                    designation.standardsDesignationId ? (
                                                                        <p className="text-danger">
                                                                            Click Save to delete
                                                                        </p>
                                                                    ) : (
                                                                        <Button
                                                                            color="danger"
                                                                            onClick={() =>
                                                                                deleteDesignation(designation, i)
                                                                            }
                                                                        >
                                                                            Delete Designation
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </EditRow>
                                                    ) : (
                                                        <FlexRow title={designation.designationDescription}>
                                                            <div style={{ flex: 1 }}>
                                                                {designation.designationNumber}
                                                            </div>
                                                            <div style={{ flex: 2 }}>
                                                                {designation.designationTitle}
                                                            </div>
                                                            <div className="text-right">
                                                                <ButtonDropdown
                                                                    className="text-left"
                                                                    isOpen={
                                                                        designation.standardsDesignationId ===
                                                                        dropdownOpenId
                                                                    }
                                                                    toggle={() =>
                                                                        toggleButtonDropdown(
                                                                            designation.standardsDesignationId,
                                                                        )
                                                                    }
                                                                    direction="start"
                                                                >
                                                                    <DropdownToggle caret color="info">
                                                                        <i className={'remixicon-more-2-fill'}></i>
                                                                    </DropdownToggle>
                                                                    <DropdownMenu>
                                                                        <DropdownItem
                                                                            onClick={() =>
                                                                                setDesignationWSubStandards(designation)
                                                                            }
                                                                        >
                                                                            Sub Standards
                                                                        </DropdownItem>
                                                                        <DropdownItem
                                                                            onClick={() => {
                                                                                setEditId(designation.standardsDesignationId);
                                                                                reset(designation);
                                                                                // Explicitly set boolean value
                                                                                setValue('isDeleted', designation.isDeleted || false);
                                                                            }}
                                                                        >
                                                                            Edit
                                                                        </DropdownItem>
                                                                    </DropdownMenu>
                                                                </ButtonDropdown>
                                                            </div>
                                                        </FlexRow>
                                                    )}
                                                </div>
                                            );
                                        })}
                            </Form>
                        </div>
                    </CardBody>
                </Card>
            )}
        </>
    );
};

export default DesignationCard;
